import { useMemo, useState } from 'react'
import { Modal } from '../../componentes/Modal'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import {
  useCatalogoGE,
  useAgendarGE,
  type FilaGE,
  type LineaPedidoGE,
} from '../../hooks/useGrandesEmpresas'

/**
 * Modal para agendar un pedido a un GE (B.4): catálogo completo SIN bonos (R9),
 * el operador elige productos + cantidades y el día, y se publica. Reutiliza el
 * flujo de publicación del agendamiento vía el endpoint /ge/:id/agendar.
 */
export function ModalAgendarGE({ ge, onCerrar }: { ge: FilaGE; onCerrar: () => void }) {
  const catalogo = useCatalogoGE(true)
  const agendar = useAgendarGE()
  const [fecha, setFecha] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [cantidades, setCantidades] = useState<Record<string, number>>({})
  const [busqueda, setBusqueda] = useState('')

  const productos = catalogo.data?.items ?? []
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return q ? productos.filter((p) => p.nombre.toLowerCase().includes(q)) : productos
  }, [productos, busqueda])

  const lineas: LineaPedidoGE[] = useMemo(
    () =>
      productos
        .filter((p) => (cantidades[String(p.id)] ?? 0) > 0)
        .map((p) => ({
          nombre: p.nombre,
          cantidad: cantidades[String(p.id)],
          precio: p.precio,
          product_id: p.id,
        })),
    [productos, cantidades],
  )

  const totalConIva = useMemo(
    () =>
      productos.reduce((sum, p) => {
        const q = cantidades[String(p.id)] ?? 0
        return sum + q * p.precioConIva
      }, 0),
    [productos, cantidades],
  )

  function setCantidad(id: string, n: number) {
    setCantidades((prev) => ({ ...prev, [id]: Math.max(0, n) }))
  }

  function confirmar() {
    if (lineas.length === 0 || !fecha) return
    agendar.mutate(
      { contactId: ge.contactId, fecha, lineas },
      { onSuccess: () => onCerrar() },
    )
  }

  return (
    <Modal titulo={`Agendar pedido · ${ge.nombre}`} onCerrar={onCerrar} anchoMax="max-w-3xl">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-tinta">Agendar pedido</h3>
          <p className="mt-0.5 text-xs text-tinta-apagada">
            {ge.nombre} · CIF {ge.cif || '—'} · {ge.poblacion || 'sin población'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
        >
          ✕
        </button>
      </div>

      {/* Día + búsqueda */}
      <div className="flex flex-wrap items-center gap-3 border-b border-borde px-5 py-3">
        <label className="flex items-center gap-2 text-sm text-tinta-2">
          Día de reparto
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="rounded-md border border-borde-fuerte bg-superficie px-2 py-1 text-sm text-tinta"
          />
        </label>
        <input
          type="search"
          placeholder="Buscar producto…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="min-w-40 flex-1 rounded-md border border-borde-fuerte bg-superficie px-3 py-1.5 text-sm text-tinta placeholder:text-tinta-apagada"
        />
      </div>

      {/* Catálogo sin bonos */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
        {catalogo.isPending ? (
          <Cargando mensaje="Cargando catálogo" />
        ) : catalogo.isError ? (
          <EstadoError error={catalogo.error} onReintentar={() => void catalogo.refetch()} />
        ) : filtrados.length === 0 ? (
          <p className="py-6 text-center text-sm text-tinta-apagada">Sin productos que coincidan.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {filtrados.map((p) => {
              const id = String(p.id)
              const q = cantidades[id] ?? 0
              return (
                <li
                  key={id}
                  className={[
                    'flex items-center gap-3 rounded-lg border px-3 py-2',
                    q > 0 ? 'border-acento bg-acento-suave' : 'border-borde-suave bg-lienzo',
                  ].join(' ')}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-tinta">{p.nombre}</p>
                    <p className="text-xs text-tinta-apagada">{p.precioConIva.toFixed(2)} € / ud (IVA incl.)</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Quitar ${p.nombre}`}
                      onClick={() => setCantidad(id, q - 1)}
                      className="size-7 rounded-md border border-borde-fuerte text-tinta-2 hover:bg-elevado"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={0}
                      value={q}
                      onChange={(e) => setCantidad(id, Number(e.target.value))}
                      className="w-14 rounded-md border border-borde-fuerte bg-superficie px-2 py-1 text-center text-sm text-tinta tabular-nums"
                    />
                    <button
                      type="button"
                      aria-label={`Añadir ${p.nombre}`}
                      onClick={() => setCantidad(id, q + 1)}
                      className="size-7 rounded-md border border-borde-fuerte text-tinta-2 hover:bg-elevado"
                    >
                      +
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Pie: resumen + confirmar */}
      <div className="flex items-center justify-between gap-3 border-t border-borde px-5 py-4">
        <div className="text-sm text-tinta-2">
          {lineas.length === 0 ? (
            'Sin productos seleccionados'
          ) : (
            <>
              <span className="font-semibold text-tinta">{lineas.length}</span> producto{lineas.length === 1 ? '' : 's'} ·{' '}
              <span className="font-semibold text-tinta">{totalConIva.toFixed(2)} €</span> (IVA incl.)
            </>
          )}
        </div>
        <button
          type="button"
          onClick={confirmar}
          disabled={lineas.length === 0 || !fecha || agendar.isPending}
          className="rounded-lg bg-acento px-4 py-2 text-sm font-medium text-tinta-inversa hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {agendar.isPending ? 'Agendando…' : 'Agendar reparto'}
        </button>
      </div>

      {agendar.isError ? (
        <p role="alert" className="px-5 pb-4 text-sm text-critico-tinta">
          No se pudo agendar el pedido. Inténtalo de nuevo.
        </p>
      ) : null}
    </Modal>
  )
}
